import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, type PanInfo } from "framer-motion";
import { ChevronsDown } from "lucide-react";
import { PackVisual } from "../fx/PackVisual";
import { sfx } from "../../lib/sfx";
import { EASE, seeded } from "../../lib/motion";
import { useIsDesktop, useIsTouch, usePrefersReducedMotion } from "../../hooks/useBreakpoint";

/** Tear line as a percentage of pack height. */
const TEAR_Y = 17;
const DRAG_RANGE = 150;
const DRAG_TRIGGER = 82;
const RIP_HANDOFF_MS = 980;
const RIP_HANDOFF_REDUCED_MS = 220;

/** Builds the ragged foil edge shared by both halves so they interlock. */
function tearPoints(seedBase: number, steps = 20): [number, number][] {
  return Array.from({ length: steps + 1 }, (_, i) => {
    const x = (i / steps) * 100;
    const jitter = i === 0 || i === steps ? 0 : (seeded(i, seedBase) - 0.5) * 6;
    return [x, TEAR_Y + jitter] as [number, number];
  });
}

function clipTop(points: [number, number][]): string {
  const edge = [...points].reverse().map(([x, y]) => `${x.toFixed(2)}% ${y.toFixed(2)}%`);
  return `polygon(0% 0%, 100% 0%, ${edge.join(", ")})`;
}

function clipBottom(points: [number, number][]): string {
  const edge = points.map(([x, y]) => `${x.toFixed(2)}% ${y.toFixed(2)}%`);
  return `polygon(${edge.join(", ")}, 100% 100%, 0% 100%)`;
}

/**
 * Step 2 — the rip. Drag the pack down (or hit the button) to tear the foil
 * along a ragged seam; the two halves fly apart and shed foil confetti.
 */
export function PackRip({
  packName,
  packImage,
  packId,
  foilLabel,
  foilColor,
  packCount,
  onTorn,
}: {
  packName: string;
  packImage: string | null;
  packId: string;
  foilLabel?: string | null;
  foilColor?: string | null;
  packCount: number;
  onTorn: () => void;
}) {
  const isDesktop = useIsDesktop();
  const isTouch = useIsTouch();
  const reduced = usePrefersReducedMotion();
  const [pull, setPull] = useState(0);
  const [torn, setTorn] = useState(false);
  const doneRef = useRef(false);
  const handoffTimerRef = useRef<number | null>(null);
  const onTornRef = useRef(onTorn);
  onTornRef.current = onTorn;

  const packImages = useMemo(() => (packImage ? [packImage] : []), [packImage]);
  const seams = useMemo(
    () => Array.from({ length: packCount }, (_, i) => tearPoints(i + 2)),
    [packCount],
  );

  const clearHandoffTimer = useCallback(() => {
    if (handoffTimerRef.current != null) {
      window.clearTimeout(handoffTimerRef.current);
      handoffTimerRef.current = null;
    }
  }, []);

  const finishRip = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    clearHandoffTimer();
    sfx.rip();
    onTornRef.current();
  }, [clearHandoffTimer]);

  useEffect(() => {
    doneRef.current = false;
    setPull(0);
    setTorn(false);
    clearHandoffTimer();
  }, [packId, packCount, clearHandoffTimer]);

  useEffect(() => clearHandoffTimer, [clearHandoffTimer]);

  const scheduleHandoff = useCallback(() => {
    clearHandoffTimer();
    const delay = reduced ? RIP_HANDOFF_REDUCED_MS : RIP_HANDOFF_MS;
    handoffTimerRef.current = window.setTimeout(finishRip, delay);
  }, [clearHandoffTimer, finishRip, reduced]);

  const tear = useCallback(() => {
    if (torn || doneRef.current) return;
    setTorn(true);
    scheduleHandoff();
  }, [scheduleHandoff, torn]);

  function onDrag(_: unknown, info: PanInfo) {
    if (torn || doneRef.current) return;
    setPull(Math.max(0, Math.min(1, info.offset.y / DRAG_RANGE)));
  }

  function onDragEnd(_: unknown, info: PanInfo) {
    if (torn || doneRef.current) return;
    if (info.offset.y >= DRAG_TRIGGER || info.velocity.y > 420) tear();
    else setPull(0);
  }

  const straining = pull > 0.25 && !torn;

  // Shrink the packs (and tighten the stack) as the multi-open count grows so
  // five packs still fit on a phone.
  const packSize =
    packCount >= 4
      ? "h-[200px] w-[140px] sm:h-[250px] sm:w-[175px] lg:h-[300px] lg:w-[210px]"
      : "h-[300px] w-[210px] sm:h-[340px] sm:w-[238px] lg:h-[380px] lg:w-[266px]";
  const stackOverlap = packCount >= 4 ? -92 : -58;
  const frontIndex = Math.round((packCount - 1) / 2);
  const tearDuration = reduced ? 0.2 : 0.9;

  return (
    <div className="flex min-h-[100dvh] w-full flex-1 flex-col px-4 py-6">
      <div className="shrink-0 text-center">
        <motion.p
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-1 font-display text-[10px] font-bold uppercase tracking-[0.3em] text-brand-pink"
        >
          Step 2 — rip it open
        </motion.p>
        <p className="max-w-xs text-center text-[11px] text-white/45">
          {isTouch ? "Drag the pack down to tear the foil." : "Pull it down, or hit the button below."}
        </p>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center py-4">
        <div className="relative flex items-center justify-center" style={{ perspective: 1200 }}>
          {seams.map((points, i) => {
            const mid = (packCount - 1) / 2;
            const offset = i - mid;
            const drift = seeded(i, 11) - 0.5;

            return (
              <motion.div
                key={i}
                className="relative"
                style={{
                  marginLeft: i === 0 ? 0 : isDesktop ? 18 : stackOverlap,
                  zIndex: i === frontIndex ? 20 : 10 - Math.abs(offset),
                }}
                initial={{ opacity: 0, y: 50, rotate: offset * 6 }}
                animate={{ opacity: 1, y: 0, rotate: torn ? 0 : offset * 6 }}
                transition={{ delay: i * 0.08, duration: 0.5, ease: EASE.outExpo }}
              >
                <div
                  className={`relative ${packSize} ${straining && i === frontIndex && !reduced ? "shake-hard" : ""}`}
                >
                  {/* top half */}
                  <motion.div
                    className="absolute inset-0"
                    style={{ clipPath: clipTop(points) }}
                    animate={
                      torn
                        ? { y: -320 - i * 20, x: drift * 220, rotate: drift * 46, opacity: 0 }
                        : { y: -pull * 26, rotate: pull * -3 }
                    }
                    transition={
                      torn
                        ? { duration: tearDuration, ease: EASE.outExpo }
                        : { type: "spring", stiffness: 260, damping: 22 }
                    }
                    onAnimationComplete={
                      torn && i === frontIndex ? () => finishRip() : undefined
                    }
                  >
                    <PackVisual
                      id={packId}
                      name={packName}
                      images={packImages}
                      foilLabel={foilLabel}
                      foilColor={foilColor}
                      sweep={!torn}
                      className="h-full w-full"
                    />
                  </motion.div>

                  {/* bottom half */}
                  <motion.div
                    className="absolute inset-0"
                    style={{ clipPath: clipBottom(points) }}
                    animate={
                      torn
                        ? { y: 300 + i * 16, x: -drift * 180, rotate: -drift * 34, opacity: 0 }
                        : { y: pull * 6 }
                    }
                    transition={
                      torn
                        ? { duration: tearDuration, ease: EASE.outExpo, delay: 0.05 }
                        : { type: "spring", stiffness: 260, damping: 22 }
                    }
                  >
                    <PackVisual
                      id={packId}
                      name={packName}
                      images={packImages}
                      foilLabel={foilLabel}
                      foilColor={foilColor}
                      sweep={false}
                      className="h-full w-full"
                    />
                  </motion.div>

                  {/* light spilling out of the seam as it opens */}
                  <motion.div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0"
                    style={{ top: `${TEAR_Y}%`, height: 4 }}
                    animate={{
                      opacity: torn ? [1, 0] : pull,
                      scaleY: torn ? 34 : 1 + pull * 8,
                    }}
                    transition={{ duration: torn ? tearDuration : 0.2 }}
                  >
                    <div className="h-full w-full bg-white blur-[3px]" />
                    <div className="absolute inset-0 bg-brand-pink blur-lg" />
                  </motion.div>

                  {/* foil shards */}
                  {torn && !reduced && (
                    <div aria-hidden className="pointer-events-none absolute inset-0">
                      {Array.from({ length: 14 }).map((_, s) => (
                        <motion.span
                          key={s}
                          className="absolute h-2 w-1.5 rounded-[1px]"
                          style={{
                            left: `${seeded(s, i + 5) * 100}%`,
                            top: `${TEAR_Y}%`,
                            background: s % 3 === 0 ? "#ffffff" : s % 3 === 1 ? "#dd54eb" : "#5460ed",
                          }}
                          initial={{ opacity: 1, y: 0, x: 0, rotate: 0 }}
                          animate={{
                            opacity: [1, 1, 0],
                            y: (seeded(s, 21) - 0.35) * 260,
                            x: (seeded(s, 31) - 0.5) * 300,
                            rotate: (seeded(s, 41) - 0.5) * 720,
                          }}
                          transition={{ duration: 1, ease: "easeOut" }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}

          {/* One drag surface over the whole stack — avoids missed hits on overlapping packs. */}
          {!torn && (
            <motion.div
              drag="y"
              dragConstraints={{ top: 0, bottom: DRAG_RANGE }}
              dragElastic={0.12}
              dragMomentum={false}
              onDrag={onDrag}
              onDragEnd={onDragEnd}
              onDoubleClick={tear}
              className="absolute inset-0 z-50 cursor-grab touch-none active:cursor-grabbing"
              style={{ touchAction: "none" }}
              aria-label="Drag down to tear the pack open"
            />
          )}
        </div>
      </div>

      {!torn && (
        <div className="relative z-50 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2">
          <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3">
            <motion.div
              aria-hidden
              className="text-brand-pink"
              animate={reduced ? {} : { y: [0, 8, 0], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            >
              <ChevronsDown size={22} />
            </motion.div>
            <button
              type="button"
              onClick={tear}
              className="relative z-10 w-full rounded-xl bg-gradient-brand px-6 py-3 text-sm font-bold shadow-lg shadow-brand-pink/30 transition-transform active:scale-95"
            >
              Rip it open
            </button>
            <div className="h-1 w-40 overflow-hidden rounded-full bg-white/10">
              <motion.div className="h-full bg-gradient-brand" animate={{ width: `${pull * 100}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
